/**
 * Three steps: who it is for, what is on it, and a last look before it is raised.
 *
 * The wizard shows its own running totals, and they are an estimate -- deliberately
 * labelled as one. Tax is resolved server-side against effective-dated rules the browser
 * does not have, so a total computed here would sometimes disagree with the order that
 * comes back. Showing an approximate number and saying so beats showing a precise-looking
 * one that is wrong.
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { useAuth } from '@/features/auth/store'
import { deliveryCharge, quoteDelivery } from '@/features/company/api'
import {
  createOrder,
  formatMoney,
  getCreditStatus,
  listLocations,
  orderUnitPrice,
  searchCustomers,
  searchProducts,
  type CustomerOption,
  type ProductOption,
} from '@/features/orders/api'
import { CrossSellPanel } from '@/features/recommendations/CrossSellPanel'
import { UpsellPanel } from '@/features/recommendations/UpsellPanel'
import type { Recommendation } from '@/features/recommendations/api'
import { toast } from '@/lib/toast'

interface Line {
  product: ProductOption
  quantity: number
}

const STEPS = ['Customer', 'Products', 'Review'] as const

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

function Steps({ current }: { current: number }) {
  return (
    <ol className="flex items-center gap-2 text-sm" aria-label="Progress">
      {STEPS.map((label, index) => (
        <li key={label} className="flex items-center gap-2">
          <span
            aria-current={index === current ? 'step' : undefined}
            className={
              index === current
                ? 'rounded-full bg-brand-600 px-3 py-1 text-white'
                : index < current
                  ? 'rounded-full bg-success/10 px-3 py-1 text-success'
                  : 'rounded-full bg-slate-100 px-3 py-1 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
            }
          >
            {index + 1}. {label}
          </span>
          {index < STEPS.length - 1 && <span className="text-slate-300">→</span>}
        </li>
      ))}
    </ol>
  )
}

export function OrderWizard() {
  const [step, setStep] = useState(0)
  const [customer, setCustomer] = useState<CustomerOption | null>(null)
  const [locationId, setLocationId] = useState('')
  const [deliveryDate, setDeliveryDate] = useState('')
  const [notes, setNotes] = useState('')
  const [discount, setDiscount] = useState('')
  const [delivery, setDelivery] = useState('')
  const [lines, setLines] = useState<Line[]>([])
  const [customerQuery, setCustomerQuery] = useState('')
  const [productQuery, setProductQuery] = useState('')
  const navigate = useNavigate()
  // Hides the panel for roles the matrix gives no `sales_analytics` view to. The API
  // refuses them anyway; this only saves them a 403 on every order they raise.
  const maySeeSuggestions = useAuth((state) => state.can('sales_analytics', 'read'))

  const customers = useQuery({
    queryKey: ['order-customers', customerQuery],
    queryFn: () => searchCustomers(customerQuery),
    retry: false,
  })

  const products = useQuery({
    queryKey: ['order-products', productQuery],
    queryFn: () => searchProducts(productQuery),
    enabled: step === 1,
    retry: false,
  })

  const locations = useQuery({
    queryKey: ['order-locations'],
    queryFn: listLocations,
    retry: false,
  })

  // Asked as soon as a customer is chosen, so a rep learns they are over the limit before
  // spending five minutes building a basket that cannot be approved.
  const credit = useQuery({
    queryKey: ['order-credit', customer?.id],
    queryFn: () => getCreditStatus(customer!.id),
    enabled: Boolean(customer),
    retry: false,
  })

  // What the ships-from location charges a sales order for delivery. The rep may type a
  // different figure; left empty, the server charges this rate.
  const deliveryQuote = useQuery({
    queryKey: ['delivery-quote', 'sales_order', locationId || null],
    queryFn: () => quoteDelivery('sales_order', locationId || null),
    retry: false,
  })

  // The product's own sales-order discount for the chosen location, as the server applies it.
  const unitPrice = (product: ProductOption) => orderUnitPrice(product, locationId || null)

  const estimate = useMemo(() => {
    const gross = lines.reduce(
      (sum, line) => sum + Number(orderUnitPrice(line.product, locationId || null)) * line.quantity,
      0,
    )
    return Math.max(0, gross - (Number(discount) || 0))
  }, [lines, discount, locationId])

  // `null` until the quote arrives. Worked out on the pre-tax estimate, so near a free-over
  // threshold the server, which counts tax, can waive a charge this still shows.
  const suggestedDelivery = deliveryQuote.data ? deliveryCharge(deliveryQuote.data, estimate) : null
  const deliveryOverridden = delivery.trim() !== ''
  const deliveryShown = deliveryOverridden ? Number(delivery) : suggestedDelivery

  // One key for the life of this attempt: a rep who resubmits after a timeout (the
  // flaky-signal case, not just a stray double-click) must replay onto the same order,
  // not raise a second one. A genuinely new order comes from a remounted wizard.
  const idempotencyKeyRef = useRef(crypto.randomUUID())

  const create = useMutation({
    mutationFn: () =>
      createOrder(
        {
          customer_id: customer!.id,
          location_id: locationId || null,
          delivery_date: deliveryDate || null,
          notes: notes || null,
          discount_amount: discount || '0',
          // Omitted unless typed, so the server applies the location's rate itself.
          ...(deliveryOverridden ? { shipping_amount: delivery.trim() } : {}),
          items: lines.map((line) => ({
            product_id: line.product.id,
            quantity: line.quantity,
          })),
        },
        idempotencyKeyRef.current,
      ),
    onSuccess: (order) => {
      toast.success(`${order.order_number} raised.`)
      navigate(`/orders/${order.id}`)
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. The order was not raised.',
      )
    },
  })

  const addLine = (product: ProductOption) =>
    setLines((current) => {
      const existing = current.find((line) => line.product.id === product.id)
      return existing
        ? current.map((line) =>
            line.product.id === product.id ? { ...line, quantity: line.quantity + 1 } : line,
          )
        : [...current, { product, quantity: 1 }]
    })

  // A suggestion carries everything a catalog row does, so it becomes a line the same
  // way one does -- no second code path, and no extra fetch to price it.
  const addSuggestion = (item: Recommendation) => {
    addLine({
      id: item.product_id,
      code: item.code,
      name: item.name,
      price: item.price,
      unit_of_measure: item.unit_of_measure,
      status: 'active',
    })
    toast.success(`${item.name} added.`)
  }

  const setQuantity = (productId: string, quantity: number) =>
    setLines((current) =>
      quantity <= 0
        ? current.filter((line) => line.product.id !== productId)
        : current.map((line) => (line.product.id === productId ? { ...line, quantity } : line)),
    )

  const canContinue = step === 0 ? Boolean(customer) : step === 1 ? lines.length > 0 : true

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">New sales order</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Saved as a draft. Nothing is reserved until it is approved.
        </p>
      </header>

      <Steps current={step} />

      {step === 0 && (
        <Card>
          <CardHeader title="Customer" description="Who is this order for?" />
          <CardBody className="space-y-4">
            <Field label="Search customers">
              {(props) => (
                <Input
                  {...props}
                  value={customerQuery}
                  placeholder="Name, code or city…"
                  onChange={(event) => setCustomerQuery(event.target.value)}
                />
              )}
            </Field>

            <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
              {(customers.data?.data ?? []).map((option) => (
                <li key={option.id}>
                  <button
                    type="button"
                    onClick={() => setCustomer(option)}
                    aria-pressed={customer?.id === option.id}
                    className={
                      customer?.id === option.id
                        ? 'flex w-full items-center justify-between bg-brand-500/10 px-4 py-3 text-left text-sm'
                        : 'flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800'
                    }
                  >
                    <span>
                      <span className="font-medium">{option.name}</span>
                      <span className="ml-2 text-slate-500 dark:text-slate-400">{option.code}</span>
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {option.tier}
                    </span>
                  </button>
                </li>
              ))}
              {customers.data?.data.length === 0 && (
                <li className="px-4 py-3 text-sm text-slate-500">No customers match that.</li>
              )}
            </ul>

            {credit.data && (
              <p
                className={
                  credit.data.credit_limit === null
                    ? 'text-sm text-slate-500 dark:text-slate-400'
                    : 'rounded-md bg-slate-100 px-3 py-2 text-sm dark:bg-slate-800'
                }
              >
                {credit.data.credit_limit === null
                  ? 'This customer is not on credit terms, so no limit applies.'
                  : `Owes ${formatMoney(credit.data.outstanding)} of a ${formatMoney(
                      credit.data.credit_limit,
                    )} limit — ${formatMoney(credit.data.available)} left.`}
              </p>
            )}

            <div className="grid gap-4 tablet:grid-cols-2">
              <Field label="Ships from" hint="Needed before the order can be approved.">
                {(props) => (
                  <select
                    {...props}
                    className={SELECT_CLASS}
                    value={locationId}
                    onChange={(event) => setLocationId(event.target.value)}
                  >
                    <option value="">Choose a location…</option>
                    {(locations.data?.data ?? []).map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                )}
              </Field>

              <Field label="Delivery date">
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    value={deliveryDate}
                    onChange={(event) => setDeliveryDate(event.target.value)}
                  />
                )}
              </Field>
            </div>
          </CardBody>
        </Card>
      )}

      {step === 1 && (
        <div className="grid gap-4 desktop:grid-cols-2">
          <Card>
            <CardHeader title="Catalog" description="Click to add a case to the order." />
            <CardBody className="space-y-4">
              <Field label="Search products">
                {(props) => (
                  <Input
                    {...props}
                    value={productQuery}
                    placeholder="Name or SKU…"
                    onChange={(event) => setProductQuery(event.target.value)}
                  />
                )}
              </Field>
              <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
                {(products.data?.data ?? []).map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      onClick={() => addLine(product)}
                      className="flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <span>
                        <span className="font-medium">{product.name}</span>
                        <span className="ml-2 text-slate-500 dark:text-slate-400">
                          {product.code}
                        </span>
                      </span>
                      <span className="tabular-nums">{formatMoney(unitPrice(product))}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="On this order" description={`${lines.length} line(s).`} />
            <CardBody className="space-y-3">
              {lines.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">Nothing added yet.</p>
              )}
              {lines.map((line) => (
                <div key={line.product.id} className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{line.product.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {formatMoney(unitPrice(line.product))} each
                    </p>
                  </div>
                  <label className="sr-only" htmlFor={`qty-${line.product.id}`}>
                    Quantity of {line.product.name}
                  </label>
                  <Input
                    id={`qty-${line.product.id}`}
                    type="number"
                    min={0}
                    className="w-20"
                    value={line.quantity}
                    onChange={(event) =>
                      setQuantity(line.product.id, Number(event.target.value) || 0)
                    }
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setQuantity(line.product.id, 0)}
                    aria-label={`Remove ${line.product.name}`}
                  >
                    Remove
                  </Button>
                </div>
              ))}

              <Field label="Order discount" hint="Applied across the lines before tax.">
                {(props) => (
                  <Input
                    {...props}
                    inputMode="decimal"
                    placeholder="0.00"
                    value={discount}
                    onChange={(event) => setDiscount(event.target.value)}
                  />
                )}
              </Field>

              <Field
                label="Delivery charge"
                hint={
                  suggestedDelivery === null
                    ? 'Charged on top of the goods; not taxed.'
                    : deliveryOverridden
                      ? `Overrides the ${formatMoney(String(suggestedDelivery))} rate. Clear to use the rate.`
                      : `Leave empty to charge the ${deliveryQuote.data?.source === 'location' ? "location's" : 'default'} rate: ${formatMoney(String(suggestedDelivery))}. Not taxed.`
                }
              >
                {(props) => (
                  <Input
                    {...props}
                    inputMode="decimal"
                    placeholder={suggestedDelivery === null ? '0.00' : suggestedDelivery.toFixed(2)}
                    value={delivery}
                    onChange={(event) => setDelivery(event.target.value)}
                  />
                )}
              </Field>
            </CardBody>
          </Card>
        </div>
      )}

      {step === 1 && maySeeSuggestions && (
        <CrossSellPanel
          customerId={customer?.id}
          productIds={lines.map((line) => line.product.id)}
          locationId={locationId || null}
          onAdd={addSuggestion}
        />
      )}

      {step === 1 && maySeeSuggestions && lines.length > 0 && (
        <UpsellPanel productIds={lines.map((line) => line.product.id)} onAdd={addSuggestion} />
      )}

      {step === 2 && (
        <Card>
          <CardHeader
            title="Review"
            description="The server prices this again when it is saved; tax is added there."
          />
          <CardBody className="space-y-4">
            <dl className="grid grid-cols-2 gap-4 text-sm tablet:grid-cols-4">
              <div>
                <dt className="text-xs uppercase text-slate-500">Customer</dt>
                <dd>{customer?.name}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-slate-500">Ships from</dt>
                <dd>
                  {locations.data?.data.find((location) => location.id === locationId)?.name ??
                    'Not set'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-slate-500">Delivery date</dt>
                <dd>{deliveryDate || 'Not set'}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-slate-500">Lines</dt>
                <dd>{lines.length}</dd>
              </div>
            </dl>

            <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 text-sm dark:divide-slate-800 dark:border-slate-800">
              {lines.map((line) => (
                <li key={line.product.id} className="flex justify-between px-4 py-2">
                  <span>
                    {line.quantity} × {line.product.name}
                  </span>
                  <span className="tabular-nums">
                    {formatMoney(String(Number(unitPrice(line.product)) * line.quantity))}
                  </span>
                </li>
              ))}
            </ul>

            <p className="text-right text-sm text-slate-500 dark:text-slate-400">
              Estimated before tax:{' '}
              <span className="font-semibold text-slate-900 tabular-nums dark:text-slate-100">
                {formatMoney(String(estimate))}
              </span>
            </p>
            {deliveryShown !== null && (
              <p className="text-right text-sm text-slate-500 dark:text-slate-400">
                {deliveryOverridden ? 'Plus delivery' : 'Plus delivery (rate)'}:{' '}
                <span className="font-semibold text-slate-900 tabular-nums dark:text-slate-100">
                  {deliveryShown > 0 ? formatMoney(String(deliveryShown)) : 'Free'}
                </span>
              </p>
            )}

            <Field label="Notes">
              {(props) => (
                <Input
                  {...props}
                  value={notes}
                  placeholder="Anything the warehouse should know"
                  onChange={(event) => setNotes(event.target.value)}
                />
              )}
            </Field>
          </CardBody>
        </Card>
      )}

      <div className="flex justify-between">
        <Button
          variant="secondary"
          onClick={() => (step === 0 ? navigate('/orders') : setStep(step - 1))}
        >
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button disabled={!canContinue} onClick={() => setStep(step + 1)}>
            Continue
          </Button>
        ) : (
          <Button loading={create.isPending} onClick={() => create.mutate()}>
            Raise order
          </Button>
        )}
      </div>
    </div>
  )
}
